<script>
import Detail from "./detail";
import SummaryCards from "./summary-cards.vue";
import moment from "moment";
import { filterActiveProfessionals } from "@/helpers/professionalStatus";

export default {
  props: {
    currentEmpresa: { type: Object, required: true },
    contasPagar: { type: Array, required: true },
    professionals: { type: Array, default: () => [] },
    hide: { type: Boolean, required: true },
    currentUser: { type: Object, required: true },
  },
  components: { Detail, SummaryCards },
  data() {
    return {
      titleBody: "Contas a Pagar ",
      currentContaPagar: undefined,
      totalRows: 1,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      filter: null,
      filterOn: [],
      sortBy: "id",
      sortDesc: false,
      statusFilter: null, // Armazena o filtro de status selecionado
      dateFilterFrom: null, // Data inicial do filtro de vencimento
      dateFilterTo: null, // Data final do filtro de vencimento
      categoryFilter: null, // Filtro por categoria (plano de contas)
      descricaoFilter: null, // Filtro por descrição
      pagoComFilter: null, // Filtro por "pago com"
      professionalFilter: null,
      competenciaFilter: null, // Filtro por competência (ano-mês)
      competenciaMesFilter: null, // Filtro por mês de competência
      competenciaAnoFilter: null, // Filtro por ano de competência
      selectedContas: [], // Array das contas selecionadas
      allSelected: false, // Controla se todas estão selecionadas
      filterOptions: [
        { text: 'Todos', value: null },
        { text: 'Pendentes', value: 0 },
        { text: 'Pagos', value: 1 }
      ],
      fields: [
        {
          label: "",
          key: "selected",
          sortable: false,
          tdClass: "text-center",
          thClass: "text-center",
        },
        {
          label: "Código",
          key: "id",
          sortable: true,
          tdClass: "text-right",
          thClass: "text-center",
        },
        {
          label: "Número",
          key: "numero_documento",
          sortable: true,
          tdClass: "text-right",
          thClass: "text-center",
        },
        {
          label: "Descrição",
          key: "descricao",
          sortable: true,
          tdClass: "text-left",
          thClass: "text-center",
        },
        {
          label: "Profissional",
          key: "professional.name",
          sortable: true,
          tdClass: "text-left",
          thClass: "text-center",
        },
        {
          label: "Pago com",
          key: "conta_debito.descricao",
          sortable: true,
          tdClass: "text-left",
          thClass: "text-center",
        },
        {
          label: "Vencimento",
          key: "vencimento",
          sortable: true,
          tdClass: "text-center",
          thClass: "text-center",
          formatter: "formatDateBR"
        },
        {
          label: "Competência",
          key: "competencia",
          sortable: true,
          tdClass: "text-center",
          thClass: "text-center",
          formatter: "formatCompetencia"
        },
        {
          label: "Valor R$",
          key: "valor_pagar",
          sortable: true,
          tdClass: "text-right",
          thClass: "text-center",
          formatter: "formatCurrency"
        },
        {
          label: "Situação",
          key: "status",
          sortable: true,
          tdClass: "text-center",
          thClass: "text-center",
          formatter: "formatStatus"
        },
        {
          label: "Ações",
          key: "acoes",
          sortable: false,
          tdClass: "text-center",
          thClass: "text-center",
        },
      ],
    };
  },
  computed: {
    /**
     * Total no. of records
     */
    rows() {
      return this.filteredContasPagar.length ? this.filteredContasPagar.length : 0;
    },
    clonefoot() {
      return this.filteredContasPagar.length || this.filteredContasPagar.length > 10
        ? true
        : false;
    },
    /**
     * Opções únicas de categoria para o filtro
     */
    categoryOptions() {
      const categories = this.contasPagar
        .map(item => item.conta_crebito?.descricao)
        .filter(Boolean)
        .filter((value, index, self) => self.indexOf(value) === index)
        .sort();
      
      return [
        { text: 'Todas as categorias', value: null },
        ...categories.map(cat => ({ text: cat, value: cat }))
      ];
    },
    /**
     * Opções únicas de "pago com" para o filtro
     */
    pagoComOptions() {
      const pagoComList = this.contasPagar
        .map(item => item.conta_debito?.descricao)
        .filter(Boolean)
        .filter((value, index, self) => self.indexOf(value) === index)
        .sort();
      
      return [
        { text: 'Todos', value: null },
        ...pagoComList.map(pago => ({ text: pago, value: pago }))
      ];
    },
    professionalOptions() {
      return [
        { text: 'Todos', value: null },
        { text: 'Sem profissional', value: 'none' },
        ...filterActiveProfessionals(this.professionals).map(professional => ({
          text: professional.name,
          value: professional.id
        }))
      ];
    },
    /**
     * Opções únicas de mês de competência para o filtro
     */
    competenciaMesOptions() {
      const meses = this.contasPagar
        .filter(item => item.mes_competencia)
        .map(item => item.mes_competencia)
        .filter((value, index, self) => self.indexOf(value) === index)
        .sort((a, b) => a - b); // Ordenar numericamente (1, 2, 3...)
      
      // Mapear para objetos com nome e valor, mantendo ordem cronológica
      const mesesComNome = meses.map(mes => ({
        text: new Date(2024, mes - 1).toLocaleDateString('pt-BR', { month: 'long' }),
        value: mes
      }));
      
      // Primeiro a opção "Todos os meses"
      // Depois os meses ordenados cronologicamente (Janeiro, Fevereiro, Março...)
      return [
        { text: 'Todos os meses', value: null },
        ...mesesComNome
      ];
    },
    /**
     * Opções únicas de ano de competência para o filtro
     */
    competenciaAnoOptions() {
      const anos = this.contasPagar
        .filter(item => item.ano_competencia)
        .map(item => item.ano_competencia)
        .filter((value, index, self) => self.indexOf(value) === index)
        .sort()
        .reverse(); // Mais recentes primeiro
      
      return [
        { text: 'Todos os anos', value: null },
        ...anos.map(ano => ({ text: ano.toString(), value: ano }))
      ];
    },
    /**
     * Opções únicas de competência para o filtro
     */
    competenciaOptions() {
      const competencias = this.contasPagar
        .filter(item => item.ano_competencia && item.mes_competencia)
        .map(item => {
          const ano = item.ano_competencia;
          const mes = item.mes_competencia.toString().padStart(2, '0');
          return `${ano}-${mes}`;
        })
        .filter((value, index, self) => self.indexOf(value) === index)
        .sort()
        .reverse(); // Mais recentes primeiro
      
      return [
        { text: 'Todas as competências', value: null },
        ...competencias.map(comp => {
          const [ano, mes] = comp.split('-');
          const mesNome = new Date(ano, mes - 1).toLocaleDateString('pt-BR', { month: 'long' });
          return { text: `${mesNome}/${ano}`, value: comp };
        })
      ];
    },
    /**
     * Filtra as contas a pagar baseado nos filtros selecionados
     */
    filteredContasPagar() {
      let filtered = this.contasPagar;

      // Filtro por status
      if (this.statusFilter !== null) {
        filtered = filtered.filter(item => item.status === this.statusFilter);
      }

      // Filtro por categoria
      if (this.categoryFilter !== null) {
        filtered = filtered.filter(item => item.conta_crebito?.descricao === this.categoryFilter);
      }

      // Filtro por descrição (busca parcial, case-insensitive)
      if (this.descricaoFilter) {
        const query = String(this.descricaoFilter).toLowerCase().trim();
        if (query) {
          filtered = filtered.filter(item => (item.descricao || '').toLowerCase().includes(query));
        }
      }

      // Filtro por "pago com"
      if (this.pagoComFilter !== null) {
        filtered = filtered.filter(item => item.conta_debito?.descricao === this.pagoComFilter);
      }

      if (this.professionalFilter !== null) {
        filtered = filtered.filter(item => this.professionalFilter === 'none'
          ? !item.professional_id
          : item.professional_id === this.professionalFilter);
      }

      // Filtro por competência (mês e ano separados)
      if (this.competenciaMesFilter !== null) {
        filtered = filtered.filter(item => item.mes_competencia === this.competenciaMesFilter);
      }
      
      if (this.competenciaAnoFilter !== null) {
        filtered = filtered.filter(item => item.ano_competencia === this.competenciaAnoFilter);
      }

      // Filtro por data de vencimento
      if (this.dateFilterFrom || this.dateFilterTo) {
        filtered = filtered.filter(item => {
          if (!item.vencimento) return false;
          
          const vencimento = new Date(item.vencimento);
          let isInRange = true;

          if (this.dateFilterFrom) {
            const fromDate = new Date(this.dateFilterFrom);
            isInRange = isInRange && vencimento >= fromDate;
          }

          if (this.dateFilterTo) {
            const toDate = new Date(this.dateFilterTo);
            // Adiciona 23:59:59 para incluir todo o dia
            toDate.setHours(23, 59, 59, 999);
            isInRange = isInRange && vencimento <= toDate;
          }

          return isInRange;
        });
      }

      return filtered;
    },
    // Computed para controlar o estado do checkbox "Selecionar Todos"
    selectAllState() {
      if (this.selectedContas.length === 0) {
        return false;
      } else if (this.selectedContas.length === this.filteredContasPagar.length) {
        return true;
      } else {
        return null; // Estado indeterminado
      }
    },
    // Computed para calcular o total das contas selecionadas
    totalValorSelecionado() {
      const contasSelecionadas = this.filteredContasPagar.filter(conta => 
        this.selectedContas.includes(conta.id)
      );
      
      const total = contasSelecionadas.reduce((acc, conta) => {
        const valor = conta.valor_pagar || conta.valor || 0;
        return acc + parseFloat(valor);
      }, 0);
      
      return total.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
  },
  mounted() {
    this.$emit("newTitle", this.titleBody);
    
    // Inicializar filtro de vencimento com mês atual
    const now = moment();
    this.dateFilterFrom = now.startOf('month').format('YYYY-MM-DD');
    this.dateFilterTo = now.endOf('month').format('YYYY-MM-DD');
  },
  watch: {
    // Limpa a seleção quando os filtros mudam
    filteredContasPagar() {
      this.clearSelection();
    }
  },
  methods: {
    /**
     * Search the table data with search input
     */
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    // eslint-disable-next-line no-unused-vars
    formatCurrency(value, key, item) {
      return value.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    formatDateBR(value) {
      if (!value) return '';
      return moment(value).format("DD/MM/YYYY");
    },
    formatCompetencia(value, key, item) {
      if (!item.ano_competencia || !item.mes_competencia) return '-';
      const mesNome = new Date(item.ano_competencia, item.mes_competencia - 1).toLocaleDateString('pt-BR', { month: 'short' });
      return `${mesNome}/${item.ano_competencia}`;
    },
    setContaPagar(value) {
      this.currentContaPagar = value;
      this.$refs["modal-contapagar"].show();
    },
    formatStatus(value) {
      return value === 0 ? 'Pendente' : 'Pago';
    },
    filterStatus(value) {
      this.statusFilter = value;
      this.currentPage = 1; // Reset para primeira página ao filtrar
    },
    filterByDate() {
      this.currentPage = 1; // Reset para primeira página ao filtrar
    },
    filterByCategory(value) {
      this.categoryFilter = value;
      this.currentPage = 1; // Reset para primeira página ao filtrar
    },
    filterByDescricao(value) {
      this.descricaoFilter = value;
      this.currentPage = 1; // Reset para primeira página ao filtrar
    },
    filterByPagoCom(value) {
      this.pagoComFilter = value;
      this.currentPage = 1; // Reset para primeira página ao filtrar
    },
    filterByProfessional(value) {
      this.professionalFilter = value;
      this.currentPage = 1;
    },
    filterByCompetencia(value) {
      this.competenciaFilter = value;
      this.currentPage = 1; // Reset para primeira página ao filtrar
    },
    setEdit(value) {
      this.currentContaPagar = value;
      this.$emit('edit', value);
    },
    setPay(value) {
      this.$emit('doPay', value);
    },
    setDelete(value) {
      this.$emit('doDelete', value);
    },
    setCancelarBaixa(value) {
      this.$emit('cancelarBaixa', value);
    },
    setBaixarConta(value) {
      this.$emit('baixarConta', value);
    },
    // Métodos para gerenciar seleção de contas
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedContas = [];
      } else {
        this.selectedContas = this.filteredContasPagar.map(conta => conta.id);
      }
      this.allSelected = !this.allSelected;
    },
    toggleSelectConta(contaId) {
      const index = this.selectedContas.indexOf(contaId);
      if (index > -1) {
        this.selectedContas.splice(index, 1);
      } else {
        this.selectedContas.push(contaId);
      }
      this.updateAllSelectedState();
    },
    updateAllSelectedState() {
      this.allSelected = this.selectedContas.length === this.filteredContasPagar.length && this.filteredContasPagar.length > 0;
    },
    isSelected(contaId) {
      return this.selectedContas.includes(contaId);
    },
    clearSelection() {
      this.selectedContas = [];
      this.allSelected = false;
    },
    clearAllFilters() {
      this.statusFilter = null;
      this.categoryFilter = null;
      this.descricaoFilter = null;
      this.pagoComFilter = null;
      this.professionalFilter = null;
      this.competenciaFilter = null;
      this.competenciaMesFilter = null;
      this.competenciaAnoFilter = null;
      this.dateFilterFrom = null;
      this.dateFilterTo = null;
      this.currentPage = 1; // Reset para primeira página ao limpar todos os filtros
    },
  }
};
</script>

<template>
  <div class="card-body" v-if="hide">
    <!-- Botão de Filtros -->
    <div class="row mb-3">
      <div class="col-12">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <b-button
              v-b-toggle="'collapse-filters'"
              class="mr-2"
              variant="outline-primary"
              size="sm"
            >
              <i class="bx bx-filter-alt"></i> Filtros Avançados
            </b-button>
            <b-button
              @click="$emit('reloadData')"
              variant="outline-success"
              size="sm"
            >
              <i class="bx bx-refresh"></i> Atualizar
            </b-button>
          </div>
          <div class="text-muted small">
            <i class="bx bx-info-circle"></i> Use os filtros para refinar sua busca
          </div>
        </div>
      </div>
    </div>

    <!-- Filtros Colapsáveis -->
    <div class="row">
      <div class="col-12">
        <b-collapse id="collapse-filters" class="mt-1">
          <div class="card border-0 mb-4">
            <div class="card-header bg-light border-bottom">
              <h6 class="mb-0 text-dark">
                <i class="bx bx-slider-alt text-primary"></i> Filtros de Pesquisa
              </h6>
            </div>
            <div class="card-body p-4">
              <!-- Primeira linha de filtros -->
              <div class="row">
                <!-- Filtro por Situação -->
                <div class="col-md-6 col-lg-3">
                  <div class="form-group mb-3">
                    <label class="form-label text-dark font-weight-semibold mb-2">
                      <i class="bx bx-check-circle text-primary"></i> Situação
                    </label>
                    <b-form-radio-group 
                      v-model="statusFilter" 
                      @input="filterStatus" 
                      :options="filterOptions" 
                      class="mt-1"
                      stacked 
                    />
                  </div>
                </div>

                <!-- Filtro por Categoria -->
                <div class="col-md-6 col-lg-3">
                  <div class="form-group mb-3">
                    <label class="form-label text-dark font-weight-semibold mb-2">
                      <i class="bx bx-category text-success"></i> Categoria
                    </label>
                    <b-form-select
                      v-model="categoryFilter"
                      @input="filterByCategory"
                      :options="categoryOptions"
                      size="sm"
                      class="mt-1"
                    />
                  </div>
                </div>

                <!-- Filtro por Pago com -->
                <div class="col-md-6 col-lg-3">
                  <div class="form-group mb-3">
                    <label class="form-label text-dark font-weight-semibold mb-2">
                      <i class="bx bx-credit-card text-warning"></i> Pago com
                    </label>
                    <b-form-select
                      v-model="pagoComFilter"
                      @input="filterByPagoCom"
                      :options="pagoComOptions"
                      size="sm"
                      class="mt-1"
                    />
                  </div>
                </div>

                <div class="col-md-6 col-lg-3">
                  <div class="form-group mb-3">
                    <label class="form-label text-dark font-weight-semibold mb-2">
                      <i class="bx bx-user text-info"></i> Profissional
                    </label>
                    <b-form-select
                      v-model="professionalFilter"
                      @input="filterByProfessional"
                      :options="professionalOptions"
                      size="sm"
                    />
                  </div>
                </div>
              </div>

              <!-- Segunda linha de filtros - Datas -->
              <div class="row">
                <!-- Filtro por Vencimento -->
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label text-dark font-weight-semibold mb-2">
                      <i class="bx bx-calendar text-danger"></i> Período de Vencimento
                    </label>
                    <div class="row mt-1">
                      <div class="col-6">
                        <label class="form-label-sm text-muted mb-1">De:</label>
                        <b-form-input
                          v-model="dateFilterFrom"
                          @input="filterByDate"
                          type="date"
                          size="sm"
                          class="form-control-sm"
                        />
                      </div>
                      <div class="col-6">
                        <label class="form-label-sm text-muted mb-1">Até:</label>
                        <b-form-input
                          v-model="dateFilterTo"
                          @input="filterByDate"
                          type="date"
                          size="sm"
                          class="form-control-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Filtro por Competência -->
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label text-dark font-weight-semibold mb-2">
                      <i class="bx bx-calendar-check text-info"></i> Competência
                    </label>
                    <div class="row mt-1">
                      <div class="col-6">
                        <label class="form-label-sm text-muted mb-1">Mês:</label>
                        <b-form-select
                          v-model="competenciaMesFilter"
                          @input="filterByCompetencia"
                          :options="competenciaMesOptions"
                          size="sm"
                          class="form-control-sm"
                        />
                      </div>
                      <div class="col-6">
                        <label class="form-label-sm text-muted mb-1">Ano:</label>
                        <b-form-select
                          v-model="competenciaAnoFilter"
                          @input="filterByCompetencia"
                          :options="competenciaAnoOptions"
                          size="sm"
                          class="form-control-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Terceira linha de filtros - Descrição -->
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label text-dark font-weight-semibold mb-2">
                      <i class="bx bx-file text-primary"></i> Descrição
                    </label>
                    <b-form-input
                      v-model="descricaoFilter"
                      @input="filterByDescricao"
                      type="text"
                      size="sm"
                      class="mt-1"
                      placeholder="Digite a descrição"
                    />
                  </div>
                </div>
              </div>

              <!-- Resumo dos Filtros Ativos -->
              <div class="row" v-if="statusFilter !== null || categoryFilter !== null || descricaoFilter || pagoComFilter !== null || dateFilterFrom || dateFilterTo || competenciaMesFilter !== null || competenciaAnoFilter !== null">
                <div class="col-12">
                  <div class="alert alert-light border-top pt-3 pb-2 px-0 mb-0">
                    <div class="d-flex justify-content-between align-items-center">
                      <div class="d-flex flex-wrap align-items-center">
                        <strong class="text-muted mx-3">
                          <i class="bx bx-filter-alt"></i> Filtros ativos:
                        </strong>
                        <b-badge 
                          v-if="statusFilter !== null" 
                          variant="primary" 
                          class="mr-2 mb-1 px-2 py-1"
                        >
                          <i class="bx bx-check-circle"></i> {{ filterOptions.find(f => f.value === statusFilter)?.text }}
                        </b-badge>
                        <b-badge 
                          v-if="categoryFilter !== null" 
                          variant="success" 
                          class="mr-2 mb-1 px-2 py-1"
                        >
                          <i class="bx bx-category"></i> {{ categoryFilter }}
                        </b-badge>
                        <b-badge
                          v-if="descricaoFilter"
                          variant="primary"
                          class="mr-2 mb-1 px-2 py-1"
                        >
                          <i class="bx bx-file"></i> {{ descricaoFilter }}
                        </b-badge>
                        <b-badge 
                          v-if="pagoComFilter !== null" 
                          variant="warning" 
                          class="mr-2 mb-1 px-2 py-1"
                        >
                          <i class="bx bx-credit-card"></i> {{ pagoComFilter }}
                        </b-badge>
                        <b-badge 
                          v-if="dateFilterFrom" 
                          variant="danger" 
                          class="mr-2 mb-1 px-2 py-1"
                        >
                          <i class="bx bx-calendar"></i> De: {{ formatDateBR(dateFilterFrom) }}
                        </b-badge>
                        <b-badge 
                          v-if="dateFilterTo" 
                          variant="danger" 
                          class="mr-2 mb-1 px-2 py-1"
                        >
                          <i class="bx bx-calendar"></i> Até: {{ formatDateBR(dateFilterTo) }}
                        </b-badge>
                        <b-badge 
                          v-if="competenciaMesFilter !== null" 
                          variant="info" 
                          class="mr-2 mb-1 px-2 py-1"
                        >
                          <i class="bx bx-calendar-check"></i> Mês: {{ competenciaMesOptions.find(m => m.value === competenciaMesFilter)?.text }}
                        </b-badge>
                        <b-badge 
                          v-if="competenciaAnoFilter !== null" 
                          variant="info" 
                          class="mr-2 mb-1 px-2 py-1"
                        >
                          <i class="bx bx-calendar-check"></i> Ano: {{ competenciaAnoFilter }}
                        </b-badge>
                      </div>
                      <div>
                        <b-button 
                          @click="clearAllFilters" 
                          variant="outline-secondary" 
                          size="sm"
                          class="px-3"
                        >
                          <i class="bx bx-x"></i> Limpar Filtros
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-collapse>
      </div>
    </div>

    <!-- Cards de Resumo Financeiro -->
    <div class="row" v-if="hide && filteredContasPagar.length > 0">
      <div class="col-md-12">
        <SummaryCards 
          :contasPagar="filteredContasPagar" 
          :currentUser="currentUser"
        />
      </div>
    </div>

    <!-- Controles da Tabela -->
    <div class="row mb-3">
      <div class="col-sm-12 col-md-6">
        <div class="d-flex align-items-center">
          <label class="mb-0 mr-2 text-muted">Exibir:</label>
          <b-form-select
            v-model="perPage"
            size="sm"
            :options="pageOptions"
            class="w-auto"
          ></b-form-select>
          <span class="ml-2 text-muted">registros</span>
        </div>
      </div>
      <div class="col-sm-12 col-md-6">
        <div class="d-flex align-items-end justify-content-end">
          <label class="d-inline-flex align-items-center">
          Pesquisar:
          <b-form-input
          v-model="filter"
          type="search"
          placeholder="Digite aqui..."
          class="form-control form-control-sm ml-2"
          ></b-form-input>
        </label>
        </div>
      </div>
    </div>
    
    <!-- Ações em Massa -->
    <div class="row mb-3" v-if="selectedContas.length > 0">
      <div class="col-12">
        <div class="alert alert-info py-2 mb-0">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <i class="bx bx-info-circle"></i>
              <strong>{{ selectedContas.length }}</strong> conta(s) selecionada(s)
              <br>
              <small class="text-muted">
                Total selecionado: <strong>{{ totalValorSelecionado }}</strong>
              </small>
            </div>
            <div>
              <b-button 
                size="sm" 
                variant="outline-secondary" 
                @click="clearSelection"
                class="mr-2"
              >
                <i class="bx bx-x"></i> Limpar Seleção
              </b-button>
              <b-button 
                size="sm" 
                variant="success" 
                @click="$emit('pagarSelecionadas', selectedContas)"
                class="mr-1"
              >
                <i class="bx bx-check"></i> Pagar Selecionadas
              </b-button>
              <b-button 
                size="sm" 
                variant="warning" 
                @click="$emit('cancelarBaixasSelecionadas', selectedContas)"
                class="mr-1"
              >
                <i class="bx bx-close-circle"></i> Cancelar Baixas Selecionadas
              </b-button>
              <b-button 
                size="sm" 
                variant="danger" 
                @click="$emit('excluirSelecionadas', selectedContas)"
              >
                <i class="bx bx-trash"></i> Excluir Selecionadas
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Tabela de Contas a Pagar -->
    <div class="row">
      <div class="col-12">
        <div v-if="hide" class="table-responsive mb-0">
          <b-table
            :items="filteredContasPagar"
            :fields="fields"
            responsive="sm"
            :per-page="perPage"
            :current-page="currentPage"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :filter="filter"
            :filter-included-fields="filterOn"
            @filtered="onFiltered"
            :hover="true"
            :foot-clone="clonefoot"
            table-class="table-sm"
          >
          <!-- Checkbox do cabeçalho para selecionar todos -->
          <template #head(selected)="">
            <b-form-checkbox
              :checked="selectAllState"
              :indeterminate="selectAllState === null"
              @change="toggleSelectAll"
            />
          </template>
          
          <!-- Checkbox individual para cada linha -->
          <template #cell(selected)="row">
            <b-form-checkbox
              :checked="isSelected(row.item.id)"
              @change="toggleSelectConta(row.item.id)"
            />
          </template>
          
          <template #cell(valor)="row">
            <b-badge
                data-name="credito"
                class="field-status"
                v-bind:variant="row.item.valor  ? 'success' : 'success'"
                >{{
                  row.item.valor  ? formatterCurrBR(row.item.valor) : formatterCurrBR(row.item.valor)
                }}</b-badge>
            
          </template>
          <template #cell(professional.name)="row">
            <span>{{ row.item.professional?.name || 'Geral' }}</span>
          </template>
          <template #cell(competencia)="row">
            <b-badge
                v-if="row.item.ano_competencia && row.item.mes_competencia"
                variant="info"
                class="field-status"
            >
              {{ formatCompetencia(null, null, row.item) }}
            </b-badge>
            <span v-else class="text-muted">-</span>
          </template>
          <template #cell(updated_at)="row">
            <b-badge
                data-name="credito"
                class="field-status"
                v-bind:variant="row.item.updated_at  ? 'info' : 'info'"
                >{{
                  formatDateTimeBR(row.item.updated_at)  
                }}</b-badge>
            
          </template>
          <template #cell(status)="row">
            <span>
              <b-badge
                data-name="credito"
                class="field-status"
                v-bind:variant="row.item.status == 1 ? 'success' : 'danger'"
              >
                {{ row.item.status == 1 ? "Pago" : "Pendente" }}
              </b-badge
              >
            </span>
          </template>
          <template #cell(acoes)="row">
            <!-- `data.value` is the value after formatted by the Formatter -->
            <b-dropdown dropright v-bind:id="'dropdown-' + row.item.id" class="m-md-2">
              <!-- Alinhar b-dropdown a esquerda e abaixo do botão -->
              <template #button-content>
                <i data-v-6289eca4="" class="bx ri-menu-line"></i>
              </template>
              <b-dropdown-item v-b-modal.modal-contapagar @click="setContaPagar(row.item)"
                ><i class="bx ri-eraser-fill text-info"></i> Visualizar</b-dropdown-item
              >
              <b-dropdown-item 
                v-if="row.item.status === 0" 
                @click="setEdit(row.item)"
              >
                <i class="bx ri-edit-2-line text-primary"></i> Alterar
              </b-dropdown-item>
              <b-dropdown-item 
                v-if="row.item.status === 0" 
                @click="setBaixarConta(row.item)"
              >
                <i class="bx ri-check-line text-success"></i> Baixar Conta
              </b-dropdown-item>
              <b-dropdown-item 
                v-if="row.item.status === 1" 
                @click="setCancelarBaixa(row.item)"
              >
                <i class="bx ri-close-circle-line text-warning"></i> Cancelar Baixa
              </b-dropdown-item>
              <b-dropdown-item @click="setDelete(row.item)"
                ><i class="bx ri-eraser-fill text-danger"></i> Excluir</b-dropdown-item
              >
            </b-dropdown>
          </template>
          </b-table>
        </div>
      </div>
    </div>
    <!-- Paginação -->
    <div v-if="hide" class="row mt-4">
      <div class="col-12">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <small class="text-muted">
              Exibindo {{ ((currentPage - 1) * perPage) + 1 }} a 
              {{ Math.min(currentPage * perPage, filteredContasPagar.length) }} de 
              {{ filteredContasPagar.length }} registros
            </small>
          </div>
          <div>
            <b-pagination
              v-model="currentPage"
              :total-rows="rows"
              :per-page="perPage"
              size="sm"
              class="mb-0"
            ></b-pagination>
          </div>
        </div>
      </div>
    </div>
    <!-- Modal de Detalhes -->
    <b-modal
      v-if="hide"
      hide-footer
      id="modal-contapagar"
      ref="modal-contapagar"
      :title="'Detalhes da Conta a Pagar'"
      title-class="font-18"
      size="xl"
      dialog-class="modal-dialog-centered modal-xl"
    >
      <Detail :contaPagar="currentContaPagar" />
    </b-modal>
  </div>
</template>

<style>
.table {
  font-size: 0.8rem !important;
}
</style>

<style scoped>
.table-sm td, .table-sm th {
  font-size: 0.8rem !important;
  align-content: baseline;
}

.table-sm .btn {
  font-size: 0.8rem !important;
}

.table-sm .badge {
  font-size: 0.65rem !important;
}

.table-sm .dropdown-toggle {
  font-size: 0.8rem !important;
  padding: 0.2rem 0.4rem !important;
}

/* Estilos para os filtros */
.card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 600;
}

.form-control-sm, .custom-select-sm {
  border-radius: 0.375rem;
  border: 1px solid #ced4da;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control-sm:focus, .custom-select-sm:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.badge {
  font-weight: 500;
  letter-spacing: 0.025em;
}

.btn-outline-primary:hover, .btn-outline-success:hover {
  transform: translateY(-1px);
  transition: transform 0.2s ease;
}

.alert-light {
  background-color: #f8f9fa;
  border-color: #e9ecef;
}
</style>
